import { useSelector } from 'react-redux';
import { betreuerByNrSelector, skontodefinitionenByArtSelector } from 'store/lookups/selector';
import { useAngebotList } from 'api/angebot';
import * as React from 'react';
import { useBetreuerprovisionListByNr } from 'api/betreuerprovision';
import { AngebotPositionFilter, useAngebotPositionList } from 'api/angebotPosition';
import { AngebotSummary, defaultLineData, FilterValues, LineData } from '../types';
import { getBetreuerAnteil } from '../utils';

export interface AngebotSummaryProps extends FilterValues {
    enabled: boolean;
}

export const useAngebotSummary = ({
    statusList,
    datumVon,
    datumBis,
    kNr,
    konzernNr,
    kundePlzVon,
    kundePlzBis,
    kundeUmsatzTsdVon,
    kundeUmsatzTsdBis,
    kundeBranchen,
    kundeErweiterteSuchen,
    kundeInteresseFuerProdukte,
    kundeBetreuer,
    kundeTelBetreuer,
    betreuer,
    betreuerGebiet,
    telBetreuer,
    telBetreuerGebiet,
    artikelGruppeNr,
    produktgruppen,
}: AngebotSummaryProps): AngebotSummary => {
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const betreuerprovisionListByNr = useBetreuerprovisionListByNr();
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const angebotList = useAngebotList(
        {
            statusList,
            datumVon,
            datumBis,
            kNr,
            konzernNr,
            kundePlzVon,
            kundePlzBis,
            kundeUmsatzVon: kundeUmsatzTsdVon ? kundeUmsatzTsdVon * 1000 : undefined,
            kundeUmsatzBis: kundeUmsatzTsdBis ? kundeUmsatzTsdBis * 1000 : undefined,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundeBetreuerExact: kundeBetreuer,
            kundeTelBetreuerExact: kundeTelBetreuer,
            betreuerExact: betreuer,
            telBetreuerExact: telBetreuer,
            artikelGruppeNr,
            produktgruppen,
            withKunde: true,
        },
        {},
    );

    const angebotPositionFilter = React.useMemo<AngebotPositionFilter>(
        () => ({ aNrList: angebotList.data?.map((a) => a.nr) || [] }),
        [angebotList],
    );
    const angebotPositionList = useAngebotPositionList(angebotPositionFilter, { enabled: !!angebotList.data });

    return React.useMemo<AngebotSummary>(() => {
        const total: LineData = { ...defaultLineData };
        const betreuerTotal: {
            [key: number]: LineData;
        } = {};
        const telBetreuerTotal: {
            [key: number]: LineData;
        } = {};

        const angebotLineDataList: LineData[] = [];

        (angebotList.data || [])
            .sort((a, b) => (a.datum + a.nr).localeCompare(b.datum + b.nr))
            .forEach((angebot) => {
                /*
                if (![0, 1, 5, 6].includes(angebot.status)) {
                    return;
                }
                 */

                if (betreuer && betreuerGebiet && angebot.kunde?.betreuer !== betreuer) {
                    return;
                }

                if (telBetreuer && telBetreuerGebiet && angebot.kunde?.telBetreuer !== telBetreuer) {
                    return;
                }

                const filteredAngebotPositionList = (angebotPositionList.data || []).filter(
                    (ap) => ap.aNr === angebot.nr,
                );

                if (filteredAngebotPositionList.length === 0) {
                    return;
                }

                const angebotLineData: LineData = {
                    ...defaultLineData,
                    aNr: angebot.nr,
                    datum: angebot.datum,
                    kNr: angebot.kNr || 0,
                    kName: angebot.kunde?.name1 || '',
                    ablehnGrund: angebot.ablehnGrund ?? 0,
                };

                const skontoP = skontodefinitionenByArt[angebot.zahlBed]?.skP1 || 0;

                let ekNetto = 0;
                let vkNetto = 0;

                filteredAngebotPositionList.forEach((angebotPosition) => {
                    ekNetto += angebotPosition.posEkPreis * angebotPosition.posMenge;
                    vkNetto += angebotPosition.posEPreis * angebotPosition.posMenge;
                });

                // const tipp = angebot.tipp === 0 ? 0 : (vkNetto / 100) * angebot.tipp;
                const skonto = skontoP === 0 ? 0 : (vkNetto / 100) * skontoP;
                const bMarge = vkNetto - ekNetto;
                // const nMarge = bMarge - tipp - skonto - angebot.fracht - angebot.roadPr;
                const nMarge = bMarge - skonto;

                // const bAnteil = (nMarge / 100) * provision;
                const { value: bAnteil } = getBetreuerAnteil(
                    betreuerprovisionListByNr,
                    angebot.betreuer,
                    angebot.datum,
                    (betreuerTotal[angebot.betreuer]?.nMarge || 0) + nMarge,
                    betreuerTotal[angebot.betreuer]?.bAnteil || 0,
                );
                const fAnteil = nMarge - bAnteil;

                total.count += 1;

                total.ekNetto += ekNetto;
                angebotLineData.ekNetto = ekNetto;

                total.vkNetto += vkNetto;
                angebotLineData.vkNetto = vkNetto;

                total.bMarge += bMarge;
                angebotLineData.bMarge = bMarge;

                // total.tippProv += tipp;
                // angebotLineData.tippProv = tipp;

                total.skonto += skonto;
                angebotLineData.skonto = skonto;

                // total.fracht += angebot.fracht + angebot.roadPr;
                // angebotLineData.fracht = angebot.fracht + angebot.roadPr;

                total.nMarge += nMarge;
                angebotLineData.nMarge = nMarge;

                total.bAnteil += bAnteil;
                angebotLineData.bAnteil = bAnteil;

                total.fAnteil += fAnteil;
                angebotLineData.fAnteil = fAnteil;

                /*
                if (angebot.betrUm) {
                    total.betrUmCount += 1;
                    total.betrUm += vkNetto;
                }
                 */

                if (angebot.betreuer) {
                    if (!betreuerTotal[angebot.betreuer]) {
                        betreuerTotal[angebot.betreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[angebot.betreuer]?.name || betreuerByNr[angebot.betreuer]?.kz,
                        };
                    }

                    betreuerTotal[angebot.betreuer].betreuerNr = angebot.betreuer;
                    betreuerTotal[angebot.betreuer].count += 1;
                    betreuerTotal[angebot.betreuer].ekNetto += ekNetto;
                    betreuerTotal[angebot.betreuer].vkNetto += vkNetto;
                    betreuerTotal[angebot.betreuer].bMarge += bMarge;
                    // betreuerTotal[angebot.betreuer].tippProv += tipp;
                    betreuerTotal[angebot.betreuer].skonto += skonto;
                    // betreuerTotal[angebot.betreuer].fracht += angebot.fracht + angebot.roadPr;
                    betreuerTotal[angebot.betreuer].nMarge += nMarge;
                    betreuerTotal[angebot.betreuer].bAnteil += bAnteil;
                    betreuerTotal[angebot.betreuer].fAnteil += fAnteil;
                }

                if (angebot.telBetreuer) {
                    if (!telBetreuerTotal[angebot.telBetreuer]) {
                        telBetreuerTotal[angebot.telBetreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[angebot.telBetreuer]?.name,
                        };
                    }

                    telBetreuerTotal[angebot.telBetreuer].betreuerNr = angebot.telBetreuer;
                    telBetreuerTotal[angebot.telBetreuer].count += 1;
                    telBetreuerTotal[angebot.telBetreuer].ekNetto += ekNetto;
                    telBetreuerTotal[angebot.telBetreuer].vkNetto += vkNetto;
                    telBetreuerTotal[angebot.telBetreuer].bMarge += bMarge;
                    // telBetreuerTotal[angebot.telBetreuer].tippProv += tipp;
                    telBetreuerTotal[angebot.telBetreuer].skonto += skonto;
                    // telBetreuerTotal[angebot.telBetreuer].fracht += angebot.fracht + angebot.roadPr;
                    telBetreuerTotal[angebot.telBetreuer].nMarge += nMarge;
                    // telBetreuerTotal[angebot.telBetreuer].bAnteil += bAnteil;
                    telBetreuerTotal[angebot.telBetreuer].fAnteil += fAnteil;
                }

                angebotLineDataList.push(angebotLineData);
            });

        let fAnteilTotal = 0;
        Object.values(betreuerTotal).forEach((item) => {
            const provision = betreuerByNr[item.betreuerNr]?.provision || 0;
            if (provision >= 100) {
                fAnteilTotal += item.bAnteil;
            } else {
                fAnteilTotal += item.fAnteil;
            }
        });

        return {
            loading: angebotList.isLoading,
            count: total.count, // angebotList.data?.length || 0,
            total,
            betreuerTotal,
            telBetreuerTotal,
            angebotLineDataList,
            fAnteilTotal,
        };
    }, [
        betreuerprovisionListByNr,
        angebotList.isLoading,
        angebotList.data,
        angebotPositionList.data,
        skontodefinitionenByArt,
        betreuerByNr,
        betreuer,
        betreuerGebiet,
        telBetreuer,
        telBetreuerGebiet,
    ]);
};
